import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";

interface IProps {
    placeHolder: string,
    setValue: (value: string) => void,
    setAudioFile: (value: File | null) => void,
    understoodText: string | null
}

export const SpeechToText = (props: IProps) => {
    const { text, listening, start, stop, setText, audioBlob, audioFile } = useSpeechToText();

    useEffect(() => {
        if (text !== null && text.length > 0) props.setValue(text);
        if (audioFile !== null) props.setAudioFile(audioFile);
    }, [text, audioFile]);

    useEffect(() => {
        if (props.understoodText === null) setText("");
    }, [props.understoodText]);

    return (
        <div style={{ width: "100%" }}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                style={{ width: "100%" }}
                placeholder={props.placeHolder}
            />

            <Button className="w-full" icon={listening ? "pi pi-stop-circle" : "pi pi-play"} label={listening ? "Click To Stop Speaking" : "Click To Start Speaking"} type="button" style={{ width: '100px', color: "black" }} onClick={listening ? stop : start} ></Button>
        </div>
    );
}

export const useSpeechToText = () => {
    const recognitionRef = useRef<any>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);

    const [text, setText] = useState("");
    const [listening, setListening] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

    useEffect(() => {
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;

        if (!SpeechRecognition) {
            console.error("Speech recognition not supported");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onresult = (event: any) => {
            let finalTranscript = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript + " ";
                }
            }

            setText(prev => prev + finalTranscript);
        };

        recognitionRef.current = recognition;
    }, []);

    const start = async () => {
        setText("");
        setAudioBlob(null);
        audioChunksRef.current = [];

        // 🎤 Get microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // 🎙️ Start audio recording
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                audioChunksRef.current.push(event.data);
            }
        };

        mediaRecorder.onstop = () => {
            const blob = new Blob(audioChunksRef.current, {
                type: "audio/webm"
            });
            setAudioBlob(blob);
        };

        mediaRecorder.start();
        recognitionRef.current?.start();
        setListening(true);
    };

    const stop = () => {
        recognitionRef.current?.stop();
        mediaRecorderRef.current?.stop();
        setListening(false);
    };

    // 📁 Convert Blob → File (for upload)
    const audioFile = audioBlob
        ? new File([audioBlob], "speech.webm", { type: "audio/webm" })
        : null;

    return { text, listening, start, stop, setText, audioBlob, audioFile };
};